import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { SettingKey } from 'data/ppoints/constants';
import { isEqual } from 'lodash-es';
import type { SettingsToEcom, SettingType } from 'models/ppoints/settings/SettingsToEcom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { settingsSetThunk, settingsThunk } from 'redux/slices/settings-slice';
import SettingService from '../features/settings/services/SettingService';

type UseSettings = <T extends SettingType>(key?: SettingKey) => SettingsReturn<T>;

export interface SettingsReturn<T> {
    settings: T | null;
    setSettings: (data: T) => Promise<boolean>;
    setSettingsWithTypeReturn: (data: T) => Promise<T | null>;
    setSettingsBulk: (data: SettingsToEcom) => Promise<boolean>;
    isDefault: boolean | null;
    fetching: boolean;
    refetch: () => Promise<void>;
    refetchWithoutLoading: () => Promise<void>;
}

const useSettings: UseSettings = <T extends SettingType>(key?: SettingKey) => {
    const dispatch = useAppDispatch();

    const defaultsQuery = useQuery({
        queryKey: [`settings-defaults`],
        queryFn: () => SettingService.getDefaults(),
        staleTime: 10 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
    });

    const { settings, fetching } = useAppSelector((state) => state.settings);

    const setSettings = async (data: SettingType): Promise<boolean> => {
        return await dispatch(settingsSetThunk({ settings: { [key]: data } }))
            .then(() => true)
            .catch((error) => {
                try {
                    Sentry.captureException(error);
                } catch {}

                return false;
            });
    };

    const setSettingsWithTypeReturn = async (data: T): Promise<T | null> => {
        return await dispatch(settingsSetThunk({ settings: { [key]: data } }))
            .unwrap()
            .then((result) => (result?.[key] as T) ?? null)
            .catch(() => null);
    };

    const setSettingsBulk = async (data: SettingsToEcom): Promise<boolean> => {
        return await dispatch(settingsSetThunk({ settings: data }))
            .then(() => true)
            .catch(() => false);
    };

    const refetch = async () => {
        await dispatch(settingsThunk({ skipLoading: false }));
    };

    const refetchWithoutLoading = async () => {
        await dispatch(settingsThunk({ skipLoading: true }));
    };

    const isDefault = !defaultsQuery.data || fetching ? null : isEqual(defaultsQuery.data[key], settings?.[key]);

    return {
        settings: settings?.[key] as T,
        setSettings,
        setSettingsWithTypeReturn,
        setSettingsBulk,
        fetching,
        isDefault,
        refetch,
        refetchWithoutLoading,
    };
};

export default useSettings;
